import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button-v25/new';
import ChartBarIcon from '@atlaskit/icon/core/chart-bar';
import DownloadIcon from '@atlaskit/icon/core/download';
import TableIcon from '@atlaskit/icon/core/table';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import { surveyTotals } from '../../jsm-portal-playground-hr-data';
import { jsmPortalPlaygroundHrMessages as messages } from '../../jsm-portal-playground-hr-messages';
import { JsmPlaygroundHeading } from '../jsm-playground-heading';

import { SurveyChoiceChart } from './survey-choice-chart';
import { SurveyChoiceTable } from './survey-choice-table';
import { SurveyInsights } from './survey-insights';
import { SurveyStatTiles } from './survey-stat-tiles';

type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const ChartIcon: IconButtonIcon = () => <ChartBarIcon label="" size="small" />;
const TableViewIcon: IconButtonIcon = () => <TableIcon label="" size="small" />;
const ExportIcon: IconButtonIcon = () => <DownloadIcon label="" size="medium" />;

const noop = () => {};

export type SurveyQuestionView = 'chart' | 'table';

export type SurveySummaryProps = {
	questionOneView: SurveyQuestionView;
	onQuestionOneViewChange: (view: SurveyQuestionView) => void;
	isInsightsOpen: boolean;
	onInsightsOpenChange: (isOpen: boolean) => void;
	shouldFocusInsightsOnOpen?: boolean;
};

export const SURVEY_SHOW_TABLE_TEST_ID = 'jsm-hr-survey-show-table';

/**
 * The Summary tab: three stat callouts, then Question 1 with its chart/table toggle and insights.
 */
export const SurveySummary = ({
	questionOneView,
	onQuestionOneViewChange,
	isInsightsOpen,
	onInsightsOpenChange,
	shouldFocusInsightsOnOpen = true,
}: SurveySummaryProps) => {
	const intl = useIntl();
	const responseCount = intl.formatMessage(messages.responseCount, {
		count: surveyTotals.responses,
	});

	return (
		<div css={summaryStyles}>
			<SurveyStatTiles />

			<section css={resultsStyles}>
				<div css={resultsHeadingStyles}>
					<JsmPlaygroundHeading level={3} size="small">
						{intl.formatMessage(messages.questionSummaries)}
					</JsmPlaygroundHeading>
					<span
						aria-hidden="true"
						// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
						inert=""
					>
						<IconButton
							icon={ExportIcon}
							label={intl.formatMessage(messages.exportResponses)}
							onClick={noop}
							tabIndex={-1}
						/>
					</span>
				</div>

				<div css={questionCardStyles}>
					<div css={questionHeaderStyles}>
						<div css={questionTitleStyles}>
							<JsmPlaygroundHeading level={4} size="xsmall">
								{intl.formatMessage(messages.questionOne)}
							</JsmPlaygroundHeading>
							<Text size="small" color="color.text.subtle">
								{responseCount}
							</Text>
						</div>
						<div css={questionActionsStyles}>
							<SurveyInsights
								isOpen={isInsightsOpen}
								onOpenChange={onInsightsOpenChange}
								shouldFocusOnOpen={shouldFocusInsightsOnOpen}
							/>
							<div
								css={viewToggleStyles}
								role="group"
								aria-label={intl.formatMessage(messages.resultsViewLabel)}
							>
								<IconButton
									appearance="subtle"
									aria-pressed={questionOneView === 'chart'}
									icon={ChartIcon}
									isSelected={questionOneView === 'chart'}
									label={intl.formatMessage(messages.showChart)}
									onClick={() => onQuestionOneViewChange('chart')}
									spacing="compact"
								/>
								<IconButton
									appearance="subtle"
									aria-pressed={questionOneView === 'table'}
									icon={TableViewIcon}
									isSelected={questionOneView === 'table'}
									label={intl.formatMessage(messages.showTable)}
									onClick={() => onQuestionOneViewChange('table')}
									spacing="compact"
									testId={SURVEY_SHOW_TABLE_TEST_ID}
								/>
							</div>
						</div>
					</div>

					{/* Keyed so switching views remounts the body and replays its entrance. */}
					{questionOneView === 'chart' ? (
						<SurveyChoiceChart key="chart" />
					) : (
						<SurveyChoiceTable key="table" />
					)}
				</div>
			</section>
		</div>
	);
};

const summaryStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	boxSizing: 'border-box',
	width: '100%',
	paddingBlockStart: token('space.300'),
	paddingInline: token('space.400'),
});

const resultsStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

const resultsHeadingStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.200'),
});

// Not clipped: the Insights card hangs out of Question 1's header, over the chart and beyond.
const questionCardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexShrink: 0,
	gap: token('space.200'),
	boxSizing: 'border-box',
	padding: token('space.300'),
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
});

const questionHeaderStyles = css({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	gap: token('space.200'),
});

const questionTitleStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.050'),
	minWidth: 0,
});

const questionActionsStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
	gap: token('space.050'),
});

// One outlined shell around both buttons, exactly as tall as the Insights button beside it.
const viewToggleStyles = css({
	display: 'flex',
	alignItems: 'center',
	boxSizing: 'border-box',
	height: '32px',
	gap: token('space.025'),
	paddingInline: token('space.025'),
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.medium'),
	backgroundColor: token('elevation.surface'),
});
